import * as React from 'react';
import { useRef } from 'react';
import dynamic from 'next/dynamic';

import cn from 'classnames';
import ImageGallery from 'react-image-gallery';
import WrappedImage from '../WrappedImage';

import { Venues } from '@weddingspot/ws-api-client';

import { Icons } from 'components/ui';

import styles from '../gallery.module.scss';
import 'react-image-gallery/styles/css/image-gallery.css';

import GalleryVideo from './GalleryVideo';
import ImageWithPlaceholder from '../../ui/ImageWithPlaceholder';
import { formatCventImage } from '@weddingspot/ws-core-components';
import SingleImage from './SingleImage';
import rings from '../../../assets/images/rings.svg';

const ReactPlayer = dynamic(() => import('react-player/lazy'));

export const Gallery = ({ images = [], videos = [] }: Venues.VenueDetails) => {
    function mapImageToURL(image: any) {
        const imgSrc = image.landscape_image || image.image;
        return {
            src: formatCventImage({ src: imgSrc, resize: '900x450', strict: false, quality: 5 }),
            thumbnail: formatCventImage({ src: image.crop_image, resize: '92x46', strict: false }),
            credit: image.photo_credit,
            isVideo: false,
        };
    }
    const imgList = images.map(mapImageToURL);
    const galleryInstance = useRef(null);
    const videoTitleNumber = 3; // Start from zero so fours slide would be a video

    if (videos.length > 0 && images.length > 3) {
        const videoSlide = {
            src: videos[0].embedded_video as string,
            thumbnail: videos[0].embedded_video as string,
            credit: videos[0].name,
            isVideo: true,
        };
        imgList.splice(videoTitleNumber, 0, videoSlide);
    }
    const isLowPhotoDesign = images.length < 6;
    let imgPrefix = images.length > 6 ? 6 : images.length;
    if (videos.length && imgPrefix >= 3 && imgPrefix !== 6) {
        imgPrefix += 1;
    }

    const imageSlides = [...imgList, ...videos.slice(-1)];
    let list: React.ReactElement[];

    if (imgPrefix !== 1) {
        list = imageSlides.slice(0, 6).map((item, index) => {
            const isVideoImage = Boolean(index === videoTitleNumber && videos.length);
            const shift = index > videoTitleNumber && videos.length ? 1 : 0;
            const isLast = index === 5;
            return (
                <div
                    key={index}
                    className={cn(styles.gridItem, styles[`image-${imgPrefix}-${index + 1}`])}
                    onClick={() => {
                        /* @ts-ignore */
                        galleryInstance.current.slideToIndex(index + shift);
                        /* @ts-ignore */
                        galleryInstance.current.toggleFullScreen();
                    }}
                >
                    {!isVideoImage && 'src' in item && <ImageWithPlaceholder src={item.src} />}
                    {isLast && images.length + videos.length > 6 && (
                        <div className={styles.overlay}>+{images.length + videos.length - 6}</div>
                    )}
                    {isVideoImage && (
                        <div className={cn(styles.thumbnailVideo)}>
                            <ReactPlayer url={videos[0].embedded_video} width={'100%'} height={'100%'} muted={true} light={true} />
                        </div>
                    )}
                </div>
            );
        });
    } else {
        list = [
            <>
                <SingleImage
                    src={imgList[0].src}
                    onClick={() => {
                        /* @ts-ignore */
                        galleryInstance.current.slideToIndex(index + shift);
                        /* @ts-ignore */
                        galleryInstance.current.toggleFullScreen();
                    }}
                />
            </>,
        ];
    }

    const renderVideo = (index: number) => () => {
        return <GalleryVideo index={index} videos={videos} />;
    };

    const videoItems = videos.map((item, index) => ({
        renderItem: renderVideo(index),
        renderThumbInner: () => (
            <div className={cn(styles.thumbnailWrapper, styles.thumbnailVideo)}>
                <ReactPlayer url={videos[index].embedded_video} width={'100%'} height={'100%'} muted={true} light={true} />
            </div>
        ),
    }));
    
    const imageItems = imgList
        .filter((item) => !item.isVideo && (item.src || item.thumbnail))
        .map((item) => ({
            renderItem: () => (
                <div className={styles.imageWrapper}>
                    <WrappedImage src={item.src || item.thumbnail} spinner={true} unoptimized={true} layout='fill' photoCredit={item.credit}/>
                </div>
            ),
            renderThumbInner: () => (
                <div className={styles.thumbnailWrapper} style={{ backgroundImage: `url(${rings})` }}>
                    <WrappedImage src={item.thumbnail || item.src} unoptimized={true} layout='fill' />
                </div>
            ),
        }));

    const galleryItems = [
        ...imageItems.filter((item, index) => index < videoTitleNumber),
        ...videoItems.slice(0, 1),
        ...imageItems.filter((item, index) => index >= videoTitleNumber),
        ...videoItems.slice(1, videoItems.length),
    ];
    return (
        <div className={cn(styles.wrapper)}>
            <div className={cn(styles.imageGrid, styles[`imageGrid-${imgPrefix}`])}>{list}</div>

            <ImageGallery
                items={galleryItems}
                useBrowserFullscreen={false}
                showPlayButton={false}
                ref={galleryInstance}
                renderFullscreenButton={(onClick, isFullscreen) => {
                    return <Icons.Close onClick={onClick} className={`image-gallery-fullscreen-button${isFullscreen ? ' active' : ''}`} />;
                }}
            />
        </div>
    );
};
